import { useMemo, useState } from "react";

// localStorage-backed useState. Reads once on mount, writes on every
// set. JSON-encoded so it carries primitives, arrays, and small
// objects equally well. Errors (private browsing, full quota,
// malformed stored JSON) silently fall back to the default rather
// than throwing.
//
// Usage:
//   const [v, setV] = useStickyState<string>("my.key", "default");
//
// The key must be stable across renders — it's used both as the
// storage key and as the dependency for set's identity.
export function useStickyState<T>(
  key: string,
  defaultValue: T,
): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  });
  const set = useMemo(
    () => (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // ignore quota / disabled-storage errors
      }
    },
    [key],
  );
  return [value, set];
}
